import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from './LoginModal';

interface HeaderProps {
  onShowGallery?: () => void;
  onGoHome?: () => void;
  showTrialInfo?: boolean;
  trialRemaining?: number;
  hideLoginButton?: boolean;
  userPlan?: any;
  /** musician / portrait でブランド表記を切り替える */
  variant?: 'default' | 'musician' | 'portrait';
}

export const Header: React.FC<HeaderProps> = ({ onShowGallery, onGoHome, showTrialInfo = false, trialRemaining = 0, hideLoginButton = false, userPlan, variant = 'default' }) => {
  const { user, isAnonymous, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogin = () => {
    setShowLoginModal(true);
    setIsMenuOpen(false);
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };


  const handleLogout = () => {
    signOut();
    setIsMenuOpen(false);
  };

  const handleGallery = () => {
    onShowGallery?.();
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    onGoHome?.();
  };

  const scrollToId = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const loggedOutNav = (
    <>
      <button onClick={() => scrollToId('use-cases')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">できること</button>
      <button onClick={() => scrollToId('flow')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">ご利用の流れ</button>
      <button onClick={() => scrollToId('pricing')} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">料金プラン</button>
      <a href="/showcase.html" className="text-slate-600 hover:text-blue-600 transition-colors font-medium">ショーケース</a>
      <button onClick={handleLogin} className="text-slate-600 hover:text-blue-600 transition-colors font-medium">ログイン</button>
      <button
        onClick={handleLogin}
        className="rounded-full bg-gradient-to-r from-blue-600 to-fuchsia-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-blue-700 hover:to-fuchsia-600"
      >
        無料で3回お試し
      </button>
    </>
  );

  return (
    <>
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
          >
            <img
              src="/images/logo.png"
              alt="Profile Studio"
              className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
            />
            <h1 className="text-xl font-bold tracking-tight text-slate-900 leading-tight">
              Profile Studio{variant === 'musician' && (
                <span className="text-pink-800"> for Musician</span>
              )}{variant === 'portrait' && (
                <span className="text-teal-800"> for Portrait</span>
              )}
              {variant === 'default' && (
                <span className="block text-[11px] font-medium tracking-normal text-slate-500">プロフィールスタジオ</span>
              )}
            </h1>
          </button>

          {/* デスクトップ用メニュー */}
          <nav className="hidden md:flex items-center space-x-5">
            {user && !isAnonymous ? (
              <>
                <button
                  onClick={handleGallery}
                  className="text-slate-600 hover:text-blue-600 transition-colors font-medium"
                >
                  ギャラリー
                </button>
                <div className="flex items-center space-x-3">
                  <span className="text-slate-600 text-sm">
                    {user.displayName || user.email}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    ログアウト
                  </button>
                </div>
              </>
            ) : !user ? (
              loggedOutNav
            ) : showTrialInfo ? (
              <div className="flex items-center space-x-3">
                <span className="text-slate-600 text-sm">
                  {userPlan?.plan === 'free' ? '無料体験' : 
                   userPlan?.plan === 'standard' ? 'Standard プラン' :
                   userPlan?.plan === 'pro' ? 'Pro プラン' :
                   userPlan?.plan === 'pro_max' ? 'Pro Max プラン' : '無料体験'} 残り {trialRemaining} 回
                </span>
                {!hideLoginButton && (
                  <button
                    onClick={handleLogin}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    ログイン
                  </button>
                )}
              </div>
            ) : (
              !hideLoginButton && (
                <button
                  onClick={handleLogin}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  ログイン
                </button>
              )
            )}
          </nav>

          {/* モバイル：ログイン＋ハンバーガー */}
          <div className="flex items-center gap-1.5 md:hidden">
            {!user && !hideLoginButton && (
              <button
                onClick={handleLogin}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="8" r="3.2" />
                  <path d="M5 19a7 7 0 0 1 14 0" />
                </svg>
                ログイン
              </button>
            )}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-slate-600 hover:text-blue-600 hover:bg-slate-100 transition-colors"
              aria-label="メニューを開く"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* モバイルメニュー */}
            {isMenuOpen && (
              <div className="absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-lg">
                <div className="px-4 py-4 space-y-3">
                  {user && !isAnonymous ? (
                    <>
                      <button
                        onClick={handleGallery}
                        className="block w-full text-left text-slate-600 hover:text-blue-600 transition-colors font-medium py-2"
                      >
                        ギャラリー
                      </button>
                      <div className="border-t border-slate-200 pt-3">
                        <p className="text-slate-600 text-sm mb-2">
                          {user.displayName || user.email}
                        </p>
                        <button
                          onClick={handleLogout}
                          className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          ログアウト
                        </button>
                      </div>
                    </>
                  ) : !user ? (
                    <>
                      <button onClick={() => scrollToId('use-cases')} className="block w-full text-left text-slate-600 hover:text-blue-600 font-medium py-2">できること</button>
                      <button onClick={() => scrollToId('flow')} className="block w-full text-left text-slate-600 hover:text-blue-600 font-medium py-2">ご利用の流れ</button>
                      <button onClick={() => scrollToId('pricing')} className="block w-full text-left text-slate-600 hover:text-blue-600 font-medium py-2">料金プラン</button>
                      <a href="/showcase.html" className="block w-full text-left text-slate-600 hover:text-blue-600 font-medium py-2">ショーケース</a>
                      <button
                        onClick={handleLogin}
                        className="w-full rounded-full bg-gradient-to-r from-blue-600 to-fuchsia-500 px-4 py-2 text-white font-semibold"
                      >
                        無料で3回お試し
                      </button>
                    </>
                  ) : (
                    !hideLoginButton && (
                      <button
                        onClick={handleLogin}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        ログイン
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </header>
    
    {/* ログインモーダル - ヘッダーの外に配置 */}
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      onLoginSuccess={handleLoginSuccess}
    />
    </>
  );
};